import React, { useCallback, useEffect, useState } from "react";
import { Plus, Sliders, TrashSimple, Pencil, Trash, CaretUp, CaretDown } from "phosphor-react";
import TableWrapper from "components/common/table-wrapper";
import { DEBOUNCE_DELAY, initialMetaForTable } from "constants/common";
import CustomModal from "components/common/modal";
import {
  useNavigate,
  createSearchParams,
  useSearchParams,
} from "react-router-dom";
import {
  formatDate,
  highlightFirstMatch,
  getUserRole,
  canDeletePropertyDatabase,
  isAdminUser,
  formatString,
  capitalizeName,
} from "utils/common";
import Tooltip from "components/common/tooltip";
import HousingDatabaseModal from "./addhousingdata/HousingDatabaseModal";
import {
  createHousingDatabases,
  deleteBulkHousingDatabases,
  deleteHousingDatabases,
  getHousingDatabases,
  updateHousingDatabases,
} from "containers/housing-expense-requests/api";
import FilterModal from "components/common/filterModal";
import { debounce } from "lodash";

const SortableTableHeader = ({
  columnKey,
  label,
  sortConfig,
  onSortChange,
  isSortable,
}) => {
  if (!isSortable) {
    return <th>{label}</th>;
  }

  const isSorting = sortConfig.key === columnKey;
  const isAscending = isSorting && sortConfig.direction === "asc";
  const isDescending = isSorting && sortConfig.direction === "desc";

  return (
    <th
      onClick={() => onSortChange(columnKey)}
      style={{ cursor: "pointer" }}
    >
      <div className="d-flex align-items-center gap-1">
        <span>{label}</span>
        {isSorting && (
          <div className="d-flex flex-column" style={{ fontSize: "10px", lineHeight: 1 }}>
            <CaretUp
              size={12}
              weight="fill"
              color={isAscending ? "#000000" : "#00000050"}
            />
            <CaretDown
              size={12}
              weight="fill"
              color={isDescending ? "#000000" : "#00000050"}
            />
          </div>
        )}
      </div>
    </th>
  );
};

const columns = [
  { label: "Property Name", columnKey: "property_name", isSortable: true },
  {
    label: "Contact",
    columnKey: "contact",
    isSortable: true,
    render: (row) => (
      <>
        <div>
          <strong>Phone:</strong> {row.phone || "N/A"}
        </div>
        <div>
          <strong>Email:</strong> {row.email || "N/A"}
        </div>
      </>
    ),
  },
  {
    label: "Address",
    columnKey: "full_address",
    isSortable: true,
    render: (row) =>
      formatString(
        highlightFirstMatch(row.full_address, row.searchQuery || ""),
      ),
  },
  {
    label: "Avail. Units",
    columnKey: "available_units",
    isSortable: true,
    render: (row) => (
      <div>
        {row?.available_units || 0}
        {row?.unit_latest_updated_at && (
          <p>{new Date(row?.unit_latest_updated_at).toLocaleDateString("en-US")}</p>
        )}
      </div>
    ),
  },
  {
    label: "No. of Units",
    columnKey: "number_of_units",
    isSortable: true,
    render: (row) => <div>{row.number_of_units || 0}</div>,
  },
  { label: "Status", columnKey: "status", isSortable: false },
  { label: "", columnKey: "actions", isSortable: false },
];

let timeout;

const HousingDatabaseCom = () => {
  const [housingList, setHousingList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [meta, setMeta] = useState({
    ...initialMetaForTable,
    mif_status: "active",
    perPage: "10",
  });
  const [loading, setLoading] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReferralIds, setSelectedReferralIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "property_name", direction: "asc" });
  const [refreshPage, setRefreshPage] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({
    zipcode_between: "",
    zipcode_and: "",
    county: "",
    rent: [],
    income: [],
    noEviction: null,
    creditScore: { checked: null, subItem: [] },
    creditCheck: null,
    ageRequirement: { checked: null, subItem: [] },
    securityDeposit: null,
    applicationFee: null,
    background: null,
    rentHistory: null,
    partyChecks: null,
    noBankruptcy: null,
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userRole = getUserRole();
  const canDelete = canDeletePropertyDatabase(userRole);
  const isAdminRole = userRole === "Admin";

  const debounceFn = (callback, delay) => {
    clearTimeout(timeout);
    timeout = setTimeout(callback, delay);
  };

  const handleSetSearchQuery = (value) => {
    setMeta((pre) => ({ ...pre, search: value }));
    debounceFn(handleRefreshPage, DEBOUNCE_DELAY);
  };

  const handleRefreshPage = () => {
    setRefreshPage((pre) => !pre);
  };

  const handlePageChange = (value) => {
    navigate({ search: createSearchParams({ page: value }).toString() });
    handleRefreshPage();
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  const toggleSelectAll = () => {
    const newSelectAll = !selectAll;
    const updated = housingList.map((item) => ({
      ...item,
      checked: newSelectAll,
    }));
    setHousingList(updated);
    setSelectAll(newSelectAll);
    setSelectedReferralIds(
      newSelectAll ? housingList.map((item) => item.id).filter(Boolean) : [],
    );
    setSelectedCount(newSelectAll ? housingList.length : 0);
  };

  const toggleCheckbox = (index) => {
    const updated = housingList.map((item, i) =>
      i === index ? { ...item, checked: !item.checked } : item,
    );
    setHousingList(updated);
    const newSelectedIds = updated
      .filter((item) => item.checked)
      .map((item) => item.id)
      .filter(Boolean);
    setSelectedReferralIds(newSelectedIds);
    setSelectedCount(newSelectedIds.length);
  };

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const result = await getHousingDatabases({
        ...meta,
        ...filter,
        page: searchParams.get("page") || meta.page,
        key: sortConfig.key,
        order: sortConfig.direction,
      });

      if (result?.housing_databases) {
        const list = result.housing_databases.map((item) => {
          const primaryAddress =
            item.addresses?.find((a) => a.address_type === "home") ||
            item.addresses?.[0] ||
            {};

          const addressParts = [
            primaryAddress.street,
            primaryAddress.state?.name || primaryAddress.state,
            primaryAddress.county?.name || primaryAddress.county,
            primaryAddress.city?.name || primaryAddress.city,
            primaryAddress.zip_code?.name || primaryAddress.zip_code,
          ].filter(Boolean);

          const fullAddress =
            addressParts.length > 0 ? addressParts.join(", ") : "N/A";

          return {
            ...item,
            checked: selectAll || selectedReferralIds.includes(item.id),
            number_of_units:
              item.units?.reduce((sum, unit) => sum + (unit.number_of_units || 0), 0) || 0,
            available_units:
              item.units?.reduce((sum, unit) => sum + (unit.available_units || 0), 0) || 0,
            phone: item.key_contacts?.[0]?.phone || "",
            full_address: fullAddress,
          };
        });
        setHousingList(list);
        setTotalCount(result.meta?.total_count || list.length);
      } else {
        setHousingList([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Fetch Data Error:", err);
    } finally {
      setLoading(false);
    }
  }, [meta, sortConfig, searchParams, selectAll, filter, loading]);   // ← sortConfig added

  const handleModalSubmit = async (data, setErrors) => {
    try {
      const result = selectedItem
        ? await updateHousingDatabases(data, selectedItem.id, setErrors)
        : await createHousingDatabases(data, setErrors);

      if (result) {
        setModalVisible(false);
        setSelectedItem(null);
        await fetchData();
      }
    } catch (error) {
      console.error("Modal Submit Error:", error);
      setErrors({ general: "Failed to save housing database." });
    }
  };

  const handleDeleteHousingDatabases = async (id) => {
    try {
      await deleteHousingDatabases(id);
      setSelectedReferralIds((prev) => prev.filter((itemId) => itemId !== id));
      setSelectAll(false);
      setSelectedCount(0);
      await fetchData();
    } catch (error) {
      console.error("Delete Housing Database Error:", error);
    }
  };

  const handleBulkDeleteHousingDatabases = async () => {
    if (!selectedReferralIds.length) return;
    const payload = { ids: selectedReferralIds };
    try {
      await deleteBulkHousingDatabases(payload);
      setSelectedReferralIds([]);
      setSelectAll(false);
      setSelectedCount(0);
      await fetchData();
    } catch (error) {
      console.error("Bulk Delete Error:", error);
    }
  };

  // Fixed useEffect - ab sortConfig change hone par bhi data refresh hoga
  useEffect(() => {
    fetchData();
  }, [refreshPage, filter, sortConfig]);   // ← sortConfig added here

  return (
    <>
      <TableWrapper
        searchPlaceholder="Search Property Database"
        setSearhQuery={(query) => handleSetSearchQuery(query)}
        searchValue={meta.search}
        totalListCount={totalCount}
        pageSize={parseInt(meta.perPage)}
        currentPage={parseInt(searchParams.get("page")) || meta.page}
        onPageChange={handlePageChange}
        selectedItems={selectedCount}
        isLoading={loading}
        meta={meta}
        setMeta={setMeta}
        handleRefreshPage={() => fetchData()}
        actionButtons={
          selectedCount > 0
            ? [
              {
                label: `Delete ${selectedCount} Items`,
                classes: "danger-btn m-0-imp",
                icon: <TrashSimple size={24} className="me-3" />,
                handleClick: handleBulkDeleteHousingDatabases,
              },
            ]
            : [
              {
                label: "Filter",
                classes: "secondary-btn",
                icon: <Sliders size={24} className="me-2" />,
                handleClick: () => setIsModalOpen(true),
              },
              {
                label: "Add Property",
                classes: "primary-btn",
                icon: <Plus size={24} className="me-3" />,
                handleClick: () => setModalVisible(true),
              },
            ]
        }
      >
        <div className="container-fluid">
          <div className="overflow-scroll">
            <table className="table table-rows">
              <thead className="table-header" style={{ backgroundColor: "black" }}>
                <tr>
                  {canDelete && (
                    <th>
                      <div className="form-check ps-3 mb-0">
                        <input
                          className="form-check-input light-grey-background"
                          type="checkbox"
                          checked={selectAll}
                          onChange={toggleSelectAll}
                        />
                      </div>
                    </th>
                  )}
                  {columns.map((col) => (
                    <SortableTableHeader
                      key={col.columnKey}
                      columnKey={col.columnKey}
                      label={col.label}
                      sortConfig={sortConfig}
                      onSortChange={handleSort}
                      isSortable={col.isSortable}
                    />
                  ))}
                </tr>
              </thead>
              <tbody className="table-body">
                {housingList.map((housingDatabase, index) => (
                  <tr key={housingDatabase.id || index}>
                    {canDelete && (
                      <td>
                        <div className="form-check ps-3 mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={housingDatabase.checked || false}
                            onChange={() => toggleCheckbox(index)}
                          />
                        </div>
                      </td>
                    )}
                    <td className="fw-bold main-heading">
                      {highlightFirstMatch(
                        housingDatabase?.property_name || "N/A",
                        meta.search,
                      )}
                    </td>
                    <td>{columns[1].render(housingDatabase)}</td>
                    <td>{columns[2].render(housingDatabase)}</td>
                    <td>{columns[3].render(housingDatabase)}</td>
                    <td>{columns[4].render(housingDatabase)}</td>
                    <td>{capitalizeName(housingDatabase.status) || "N/A"}</td>
                    <td>
                      <Tooltip title="Edit">
                        <Pencil
                          size={24}
                          className="opacity-50 me-4"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedItem(housingDatabase);
                            setModalVisible(true);
                          }}
                        />
                      </Tooltip>

                      <Tooltip title="Delete">
                        <Trash
                          size={24}
                          className="opacity-50 me-3"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            housingDatabase?.id && handleDeleteHousingDatabases(housingDatabase.id)
                          }
                        />
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </TableWrapper>

      <CustomModal
        heading="Filter"
        className="custom-width-modal"
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        size="sm"
      >
        <FilterModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          currentFilters={filter}
          handleSubmission={setFilter}
        />
      </CustomModal>

      {modalVisible && !isAdminRole && (
        <CustomModal
          size="lg"
          show
          onHide={() => {
            setModalVisible(false);
            setSelectedItem(null);
          }}
          heading={`${selectedItem ? "Edit" : "Add"} Property`}
        >
          <HousingDatabaseModal
            handleQuestionSubmittion={handleModalSubmit}
            handleClose={() => {
              setModalVisible(false);
              setSelectedItem(null);
            }}
            selectedMember={selectedItem}
          />
        </CustomModal>
      )}
    </>
  );
};

export default HousingDatabaseCom;